/**
 * Link Integrity Registry
 * 
 * Single source of truth for all internal route paths.
 * NEVER hardcode route strings in components — always import from this file.
 * 
 * This prevents broken links when routes change and ensures consistency across the codebase.
 */

// ============================================================================
// MAIN PAGES
// ============================================================================
export const ROUTE_HOME = '/';
export const ROUTE_INVENTORY = '/inventory';
export const ROUTE_CONTACT = '/contact';
export const ROUTE_FAQ = '/faq';
export const ROUTE_REVIEWS = '/reviews';
export const ROUTE_WRITE_REVIEW = '/write-a-review';
export const ROUTE_SITEMAP = '/sitemap';

// ============================================================================
// VEHICLE PAGES
// ============================================================================
export const ROUTE_VEHICLE_DETAIL = '/inventory/:id';
export const ROUTE_VEHICLE_STICKER = '/sticker/:id';
export const ROUTE_SOLD_VEHICLES = '/sold-vehicles';
// Note: /compare-vehicles is the route registered in the router
export const ROUTE_COMPARE_VEHICLES = '/compare-vehicles';

// Helper function to generate vehicle detail URL
export function getVehicleDetailUrl(id: string): string {
  return `/inventory/${id}`;
}

export function getCompareVehiclesUrl(vehicleIds: string[]): string {
  return `/compare-vehicles?ids=${vehicleIds.join(',')}`
}

export const getVehicleStickerUrl = (id: string | number) => `/sticker/${id}`;

// ============================================================================
// BUYING & SELLING
// ============================================================================
export const ROUTE_HOW_IT_WORKS = '/how-it-works';
export const ROUTE_SELL_YOUR_CAR = '/sell-your-car';
export const ROUTE_TRADE_IN = '/trade-in';
export const ROUTE_CONSIGNMENT = '/consignment';
export const ROUTE_DEAL_RADAR = '/deal-radar';

// ============================================================================
// FINANCING
// ============================================================================
export const ROUTE_FINANCING = '/financing';
export const ROUTE_APPLY_FINANCING = '/apply-for-financing';
export const ROUTE_FINANCING_APPROVAL = '/financing-approval';

// ============================================================================
// BROKER SERVICES
// ============================================================================
export const ROUTE_BROKER_SERVICES = '/broker-services';
export const ROUTE_BROKER_REQUEST = '/broker-services/request-a-vehicle';
export const ROUTE_WHY_BROKER = '/broker-services/why-broker';
export const ROUTE_WHY_USE_BROKER = '/why-use-a-broker';

// Broker city pages
export const ROUTE_BROKER_PORTLAND = '/broker-services/portland';
export const ROUTE_BROKER_SEATTLE = '/broker-services/seattle';
export const ROUTE_BROKER_EUGENE = '/broker-services/eugene';
export const ROUTE_BROKER_BEND = '/broker-services/bend';
export const ROUTE_BROKER_MEDFORD = '/broker-services/medford';

// ============================================================================
// SERVICE AREAS
// ============================================================================
export const ROUTE_SERVICE_AREAS = '/service-areas';
export const ROUTE_SERVICE_WARRENTON = '/service-areas/warrenton';
export const ROUTE_SERVICE_ASTORIA = '/service-areas/astoria';
export const ROUTE_SERVICE_SEASIDE = '/service-areas/seaside';
export const ROUTE_SERVICE_CANNON_BEACH = '/service-areas/cannon-beach';
export const ROUTE_SERVICE_GEARHART = '/service-areas/gearhart';
export const ROUTE_SERVICE_TILLAMOOK = '/service-areas/tillamook';
export const ROUTE_SERVICE_CLATSOP_COUNTY = '/service-areas/clatsop-county';
export const ROUTE_SERVICE_PORTLAND = '/service-areas/portland';
export const ROUTE_SERVICE_ILWACO = '/service-areas/ilwaco';
export const ROUTE_SERVICE_LONG_BEACH_WA = '/service-areas/long-beach-wa';

// ============================================================================
// LOCATION-SPECIFIC PAGES
// ============================================================================
export const ROUTE_USED_CARS_ASTORIA = '/used-cars-astoria-oregon';
export const ROUTE_USED_CARS_SEASIDE = '/used-cars-seaside-oregon';
export const ROUTE_USED_CARS_TILLAMOOK = '/used-cars-tillamook-oregon';
export const ROUTE_USED_CARS_PORTLAND = '/used-cars-portland-oregon';
export const ROUTE_USED_CARS_ILWACO = '/used-cars-ilwaco-washington';
export const ROUTE_USED_CARS_LONGVIEW = '/used-cars-longview-washington';
export const ROUTE_USED_CARS_SW_WASHINGTON = '/used-cars-sw-washington';
export const ROUTE_USED_CARS_WARRENTON = '/used-cars-warrenton-oregon';
export const ROUTE_USED_SUVS_WARRENTON = '/used-suvs-warrenton-oregon';
export const ROUTE_USED_TRUCKS_WARRENTON = '/used-trucks-warrenton-oregon';

// ============================================================================
// DELIVERY
// ============================================================================
export const ROUTE_OREGON_COAST_DELIVERY = '/oregon-coast-delivery';

// ============================================================================
// BUYER GUIDES & CONTENT
// ============================================================================
export const ROUTE_BLOG = '/blog';
export const ROUTE_BLOG_ARTICLE = '/blog/:slug';
export const ROUTE_BLOG_NEWSLETTER = '/blog/newsletter';
export const ROUTE_CAR_BUYING_GUIDE = '/car-buying-guide';
// Router uses /car-buying-guide/:slug (no /article/ segment)
export const ROUTE_CAR_BUYING_GUIDE_ARTICLE = '/car-buying-guide/:slug';
export const ROUTE_OREGON_CAR_BUYING_GUIDE = '/oregon-car-buying-guide';
export const ROUTE_FIRST_TIME_BUYER_GUIDE = '/first-time-buyer-guide';

// Helper function to generate blog article URL
export const getBlogArticleUrl = (slug: string) => `/blog/${slug}`;
// Matches router path: /car-buying-guide/:slug
export const getCarBuyingGuideArticleUrl = (slug: string) => `/car-buying-guide/${slug}`;

// ============================================================================
// PRICE RANGE PAGES
// ============================================================================
export const ROUTE_CARS_UNDER_10K = '/cars-under-10k';
export const ROUTE_BEST_USED_CARS_UNDER_15K = '/best-used-cars-under-15k';
export const ROUTE_USED_CARS_UNDER_20K = '/used-cars-under-20000';
export const ROUTE_BEST_USED_SUVS_UNDER_20K = '/best-used-suvs-under-20k';
export const ROUTE_BEST_AWD_CARS_UNDER_25K = '/best-awd-cars-under-25k';
export const ROUTE_CARS_UNDER_30K = '/cars-under-30k';
export const ROUTE_BEST_TRUCKS_UNDER_30K = '/best-trucks-under-30k-oregon';

// ============================================================================
// CATEGORY & COMPARISON PAGES
// ============================================================================
export const ROUTE_BEST_USED_CARS_OREGON_COAST = '/best-used-cars-oregon-coast';
export const ROUTE_BEST_CARS_OREGON_COAST_WEATHER = '/best-cars-oregon-coast-weather';
export const ROUTE_BEST_USED_SUVS_COASTAL_WEATHER = '/best-used-suvs-coastal-weather';
export const ROUTE_AWD_VEHICLES_OREGON_COAST = '/awd-vehicles-oregon-coast';
export const ROUTE_BEST_AWD_CARS_OREGON_WASHINGTON = '/best-awd-cars-oregon-washington';
export const ROUTE_BEST_FAMILY_CARS_OREGON = '/best-family-cars-oregon';
export const ROUTE_FAMILY_SUVS_OREGON = '/family-suvs-oregon';
export const ROUTE_BEST_CARS_COMMUTING_PORTLAND = '/best-cars-commuting-portland';
export const ROUTE_BEST_FIRST_TIME_BUYER_CARS = '/best-first-time-buyer-cars';
export const ROUTE_TOYOTA_RAV4_VS_HONDA_CRV = '/toyota-rav4-vs-honda-crv-oregon';

// ============================================================================
// COMPETITOR COMPARISON
// ============================================================================
export const ROUTE_COMPARE = '/compare';
export const ROUTE_COMPETITOR_BASE = '/vs';
export const ROUTE_COMPETITOR = '/vs/:slug';
export const ROUTE_VS_ASTORIA_FORD = '/vs/astoria-ford';
export const ROUTE_VS_LUMS_TOYOTA = '/vs/lums-toyota';
export const ROUTE_VS_OCEANCREST_CHEVROLET = '/vs/oceancrest-chevrolet';
export const ROUTE_VS_WARRENTON_KIA = '/vs/warrenton-kia';

// Helper function to generate competitor URL
export const getCompetitorUrl = (slug: string) => `/vs/${slug}`;

// ============================================================================
// COMMUNITY & SOCIAL
// ============================================================================
export const ROUTE_COMMUNITY_IMPACT = '/community-impact';
export const ROUTE_CUSTOMER_STORIES = '/customer-stories';
// Router uses /customer-stories/:slug (not /detail/:id)
export const ROUTE_CUSTOMER_STORY_DETAIL = '/customer-stories/:slug';
export const ROUTE_REFERRAL = '/referral';
export const ROUTE_VIDEOS = '/videos';
export const ROUTE_VLOG = '/vlog';
export const ROUTE_MILITARY_DISCOUNT = '/military-discount'; // Dog Tag Discount

// Helper function to generate customer story URL — matches router /customer-stories/:slug
export const getCustomerStoryUrl = (slug: string | number) => `/customer-stories/${slug}`;

// ============================================================================
// THANK YOU PAGES
// ============================================================================
export const ROUTE_THANK_YOU = '/thank-you';
export const ROUTE_THANK_YOU_FINANCING = '/thank-you/financing';
export const ROUTE_THANK_YOU_TRADE_IN = '/thank-you/trade-in';
export const ROUTE_THANK_YOU_REFERRAL = '/thank-you/referral';
export const ROUTE_THANK_YOU_CHECK_AVAILABILITY = '/thank-you/check-availability';
export const ROUTE_THANK_YOU_SELL_YOUR_CAR = '/thank-you/sell-your-car';
export const ROUTE_THANK_YOU_DEAL_RADAR = '/thank-you/deal-radar';
export const ROUTE_THANK_YOU_MILITARY = '/thank-you/military';
export const ROUTE_THANK_YOU_DOG_TAG_REFERRAL = '/thank-you/dog-tag-referral';

// ============================================================================
// LEGAL
// ============================================================================
export const ROUTE_PRIVACY_POLICY = '/privacy-policy';
export const ROUTE_TERMS_OF_SERVICE = '/terms-of-service';

// ============================================================================
// ABOUT & BRAND
// ============================================================================
export const ROUTE_ABOUT_OWNER = '/about-the-owner';
export const ROUTE_WHY_THE_DOGS = '/why-the-dogs';

// ============================================================================
// ADMIN PAGES
// ============================================================================
export const ROUTE_ADMIN_DASHBOARD = '/admin/dashboard';
export const ROUTE_ADMIN_INVENTORY = '/admin/inventory';
export const ROUTE_ADMIN_REVIEWS = '/admin/reviews';
export const ROUTE_ADMIN_REVIEWS_ANALYTICS = '/admin/reviews/analytics';
export const ROUTE_ADMIN_CONTENT = '/admin/content';
export const ROUTE_ADMIN_IMAGES = '/admin/images';
export const ROUTE_ADMIN_FEEDS = '/admin/feeds';
export const ROUTE_ADMIN_DMS = '/admin/dms';
export const ROUTE_ADMIN_FAVICON = '/admin/favicon';
export const ROUTE_ADMIN_PAGE_HEALTH = '/admin/page-health';
export const ROUTE_ADMIN_PAGE_PREVIEW = '/admin/page-preview';
export const ROUTE_ADMIN_REFERRALS = '/admin/referrals';
export const ROUTE_ADMIN_STICKER_ANALYTICS = '/admin/sticker-analytics';
export const ROUTE_ADMIN_WINDOW_STICKER = '/admin/window-sticker';
export const ROUTE_ADMIN_SELL_OFFERS = '/admin/sell-offers';

// ============================================================================
// EXTERNAL LINKS (for reference)
// ============================================================================
export const EXTERNAL_FACEBOOK = 'https://facebook.com/rockhoundautos';
export const EXTERNAL_INSTAGRAM = 'https://instagram.com/rockhoundautos';
export const EXTERNAL_YOUTUBE = 'https://youtube.com/@rockhoundautos';
export const EXTERNAL_GOOGLE_REVIEWS = 'https://g.page/r/YOUR_GOOGLE_PLACE_ID/review';

// ============================================================================
// ROUTE GROUPS (for navigation menus)
// ============================================================================
export const BUYING_ROUTES = [
  ROUTE_INVENTORY,
  ROUTE_HOW_IT_WORKS,
  ROUTE_FINANCING,
  ROUTE_BROKER_SERVICES,
  ROUTE_DEAL_RADAR,
] as const;

export const SELLING_ROUTES = [
  ROUTE_SELL_YOUR_CAR,
  ROUTE_TRADE_IN,
  ROUTE_CONSIGNMENT,
] as const;

export const SERVICE_AREA_ROUTES = [
  ROUTE_SERVICE_WARRENTON,
  ROUTE_SERVICE_ASTORIA,
  ROUTE_SERVICE_SEASIDE,
  ROUTE_SERVICE_CANNON_BEACH,
  ROUTE_SERVICE_GEARHART,
  ROUTE_SERVICE_TILLAMOOK,
  ROUTE_SERVICE_CLATSOP_COUNTY,
  ROUTE_SERVICE_PORTLAND,
  ROUTE_SERVICE_ILWACO,
  ROUTE_SERVICE_LONG_BEACH_WA,
] as const;

export const CONTENT_ROUTES = [
  ROUTE_BLOG,
  ROUTE_CAR_BUYING_GUIDE,
  ROUTE_CUSTOMER_STORIES,
  ROUTE_VIDEOS,
  ROUTE_VLOG,
] as const;

export const ADMIN_ROUTES = [
  ROUTE_ADMIN_DASHBOARD,
  ROUTE_ADMIN_INVENTORY,
  ROUTE_ADMIN_REVIEWS,
  ROUTE_ADMIN_CONTENT,
  ROUTE_ADMIN_IMAGES,
] as const;